import React from 'react';
import { Star, ExternalLink, Map, Globe } from 'lucide-react';
import { FadeInUp, BounceIn } from './ScrollAnimationWrapper';

const Testimonials = ({ language = 'de' }) => {
  
  const translations = {
    de: {
      title: 'Bewertungen',
      subtitle: 'Sehen Sie, was unsere Gäste sagen',
      readReviews: 'Bewertungen Lesen',
      bottomInfo: 'Über 200+ positive Bewertungen von zufriedenen Gästen'
    },
    en: {
      title: 'Reviews',
      subtitle: 'See what our guests say',
      readReviews: 'Read Reviews',
      bottomInfo: 'Over 200+ positive reviews from satisfied guests'
    }
  };

  const reviewPlatforms = [
    {
      name: 'Google Maps',
      icon: Map,
      url: 'https://www.google.com/search?q=la+casa+hattingen&oq=la+casa+&gs_lcrp=EgZjaHJvbWUqDggAEEUYJxg7GIAEGIoFMg4IABBFGCcYOxiABBiKBTIKCAEQLhixAxiABDINCAIQLhivARjHARiABDIGCAMQRRg7MgYIBBBFGDsyBggFECMYJzIGCAYQRRg8MgYIBxBFGDzSAQgxNTAyajBqN6gCALACAA&sourceid=chrome&ie=UTF-8',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500',
      hoverBg: 'hover:bg-green-600'
    },
    {
      name: 'TripAdvisor',
      icon: Globe,
      url: 'https://www.tripadvisor.com/Hotel_Review-g1051820-d3706313-Reviews-Haus_Niggemann-Hattingen_North_Rhine_Westphalia.html',
      color: 'from-green-600 to-teal-600',
      bgColor: 'bg-green-600',
      hoverBg: 'hover:bg-green-700'
    },
    {
      name: 'HRS',
      icon: Star,
      url: 'https://www.hrs.de/de/hotel/217605',
      color: 'from-red-500 to-pink-600',
      bgColor: 'bg-red-500',
      hoverBg: 'hover:bg-red-600'
    }
  ];

  const t = translations[language];

  return (
    <section className="py-12 md:py-16 bg-luxury-charcoal">
      <div className="container-custom">
        
        {/* Header */}
        <FadeInUp threshold={0.1} className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-3">
            {t.title}
          </h2>
          <div className="w-20 h-1 bg-luxury-gold mx-auto mb-4" />
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </FadeInUp>

        {/* Review Platforms - Elegant Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {reviewPlatforms.map((platform, index) => (
            <BounceIn key={index} delay={index * 150} threshold={0.1}>
              <a
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block relative bg-black/50 text-white p-6 rounded-2xl shadow-xl hover:shadow-luxury-gold/20 transition-all duration-500 hover:-translate-y-1 border border-luxury-gold/40 hover:border-luxury-gold overflow-hidden backdrop-blur-sm"
              >
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-luxury-charcoal/40 to-black/60 group-hover:from-luxury-charcoal/80 group-hover:to-black/80 transition-all duration-500 rounded-2xl" />
                
                {/* Animated Corner Accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-luxury-gold opacity-10 group-hover:opacity-20 transition-opacity duration-500 blur-2xl rounded-2xl" />
                
                {/* Content */}
                <div className="relative z-10 text-center">
                  {/* Icon */}
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 bg-luxury-gold/20 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300 border border-luxury-gold/40">
                      <platform.icon className="text-luxury-gold" size={32} strokeWidth={2.5} />
                    </div>
                  </div>

                  {/* Platform Name */}
                  <h3 className="text-xl font-display font-bold text-white mb-3 tracking-wide">
                    {platform.name}
                  </h3>

                  {/* Star Rating Visual */}
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className="text-luxury-gold fill-luxury-gold" 
                        size={16} 
                      />
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-center gap-2 p-3 bg-luxury-gold/15 rounded-lg border border-luxury-gold/40 group-hover:bg-luxury-gold/25 transition-all duration-300">
                    <span className="text-sm font-medium tracking-wide text-white">
                      {t.readReviews}
                    </span>
                    <ExternalLink className="text-luxury-gold" size={16} />
                  </div>
                </div>

                {/* Subtle Glow Effect on Hover */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-luxury-gold/5" />
              </a>
            </BounceIn>
          ))}
        </div>

        {/* Bottom Info */}
        <FadeInUp delay={600} threshold={0.1} className="mt-12 text-center">
          <div className="inline-block bg-black/30 backdrop-blur-sm rounded-xl px-8 py-4 border border-luxury-gold/40 shadow-lg">
            <p className="text-white font-serif text-base leading-relaxed">
              <span className="text-luxury-gold font-bold">✨</span>
              <span className="mx-2">
                {t.bottomInfo}
              </span>
              <span className="text-luxury-gold font-bold">✨</span>
            </p>
          </div>
        </FadeInUp>

      </div>
    </section>
  );
};

export default Testimonials;
